import React, { useEffect, useState } from "react";
import { BookOpenCheck, Landmark } from "lucide-react";
import { toast } from "sonner";
import { fetchAjes, proposeAje, proposeDepreciationAje, approveAje } from "../../api";

const money = (n) =>
  `${n < 0 ? "-" : ""}$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

const apiError = (e) =>
  (e.response && e.response.data && e.response.data.detail) || "Request failed";

export const AjeTab = ({ coaOptions, onPosted }) => {
  const [ajes, setAjes] = useState(null);
  const [debit, setDebit] = useState("6900");
  const [credit, setCredit] = useState("1500");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [support, setSupport] = useState("");
  const [approvers, setApprovers] = useState({});

  useEffect(() => {
    fetchAjes().then((d) => setAjes(d.ajes)).catch(() => {});
  }, []);

  if (!ajes)
    return <div className="text-sm text-zinc-400 animate-pulse p-6">Loading adjusting entries…</div>;

  const doProposeDep = async () => {
    try {
      const res = await proposeDepreciationAje();
      setAjes(res.ajes);
      toast.success(`AJE #${res.aje.number} proposed by staff_1`, {
        description: `${res.aje.description} — ${money(res.aje.lines[0].amount)} awaiting reviewer approval.`,
      });
    } catch (e) {
      toast.error("Proposal failed", { description: apiError(e) });
    }
  };

  const doPropose = async () => {
    const amt = parseFloat(amount);
    if (!amt || !description.trim() || !support.trim()) {
      toast.error("Description, support reference and a non-zero amount are required");
      return;
    }
    try {
      const res = await proposeAje(description, support, [
        { coa_account: debit, amount: amt },
        { coa_account: credit, amount: -amt },
      ]);
      setAjes(res.ajes);
      setAmount("");
      setDescription("");
      setSupport("");
      toast.success(`AJE #${res.aje.number} proposed`, { description: "Balanced entry awaiting reviewer approval." });
    } catch (e) {
      toast.error("Proposal failed", { description: apiError(e) });
    }
  };

  const doApprove = async (number) => {
    const reviewer = approvers[number] || "reviewer_1";
    try {
      const res = await approveAje(number, reviewer);
      setAjes(res.ajes);
      toast.success(`AJE #${number} approved & posted by ${reviewer}`, {
        description: "The entry now flows into the trial balance and the P&L.",
      });
      onPosted();
    } catch (e) {
      toast.error(`Approval blocked for AJE #${number}`, { description: apiError(e) });
    }
  };

  return (
    <div className="space-y-4" data-testid="aje-tab">
      <div className="glass-card rounded-md border border-white/10 p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-white/10 pb-3">
          <h3 className="text-sm font-bold text-white flex items-center">
            <BookOpenCheck className="w-4 h-4 mr-2 text-blue-400" /> Adjusting Journal Entries
          </h3>
          <button
            data-testid="propose-depreciation-aje-btn"
            onClick={doProposeDep}
            className="px-3 py-1.5 bg-amber-500/10 hover:bg-amber-200 border border-amber-500/40 text-amber-800 text-xs font-semibold rounded-md transition flex items-center space-x-1.5"
          >
            <Landmark className="w-3.5 h-3.5" />
            <span>Propose Depreciation AJE (from Form 4562)</span>
          </button>
        </div>

        <div className="space-y-3" data-testid="aje-list">
          {ajes.length === 0 && (
            <p className="text-xs text-zinc-500" data-testid="aje-empty">
              No adjusting entries yet — propose the depreciation entry or a custom one below.
            </p>
          )}
          {ajes.map((a) => (
            <div key={a.number} className={`rounded-md border p-4 text-xs space-y-2 ${a.posted ? "bg-emerald-50/50 border-emerald-500/30" : "bg-amber-50/50 border-amber-500/30"}`} data-testid={`aje-card-${a.number}`}>
              <div className="flex items-center justify-between">
                <span className="font-bold text-white">AJE #{a.number} — {a.description}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${a.posted ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"}`}>
                  {a.posted ? `Posted · ${a.reviewer}` : "Awaiting approval"}
                </span>
              </div>
              <div className="text-[11px] text-zinc-400">Support: {a.support_reference} · prepared by {a.preparer}</div>
              <div className="bg-zinc-900/70 border border-white/10 rounded-md divide-y divide-white/5">
                {a.lines.map((l, i) => (
                  <div key={i} className="flex justify-between px-3 py-1.5 font-mono text-[11px]">
                    <span className="text-zinc-400">{l.coa_account} — {l.name}</span>
                    <span className={l.side === "debit" ? "text-white font-bold" : "text-zinc-400"}>
                      {l.side === "debit" ? `Dr ${money(l.amount)}` : `Cr ${money(l.amount)}`}
                    </span>
                  </div>
                ))}
              </div>
              {!a.posted && (
                <div className="flex items-center space-x-2 pt-1">
                  <select
                    data-testid={`aje-approver-select-${a.number}`}
                    value={approvers[a.number] || "reviewer_1"}
                    onChange={(e) => setApprovers((p) => ({ ...p, [a.number]: e.target.value }))}
                    className="bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 text-xs font-semibold text-zinc-300 cursor-pointer focus:outline-none"
                  >
                    <option value="staff_1">staff_1 (preparer!)</option>
                    <option value="reviewer_1">reviewer_1</option>
                    <option value="partner_1">partner_1</option>
                  </select>
                  <button
                    data-testid={`aje-approve-btn-${a.number}`}
                    onClick={() => doApprove(a.number)}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold rounded-md transition"
                  >
                    Approve &amp; Post
                  </button>
                  <span className="text-[10px] text-zinc-500">The preparer of an entry may never approve it.</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="glass-card rounded-md border border-white/10 p-6 space-y-3">
        <h3 className="text-sm font-bold text-white border-b border-white/10 pb-3">Propose Custom Entry (auto-balanced)</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Debit account</label>
            <select data-testid="aje-debit-select" value={debit} onChange={(e) => setDebit(e.target.value)} className="w-full bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 mt-1 cursor-pointer focus:outline-none">
              {coaOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Credit account</label>
            <select data-testid="aje-credit-select" value={credit} onChange={(e) => setCredit(e.target.value)} className="w-full bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 mt-1 cursor-pointer focus:outline-none">
              {coaOptions.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Amount ($)</label>
            <input data-testid="aje-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 mt-1 focus:outline-none focus:border-blue-500" />
          </div>
        </div>
        <div className="flex items-center space-x-2 text-xs">
          <input data-testid="aje-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description (required)..." className="flex-1 bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 focus:outline-none focus:border-blue-500" />
          <input data-testid="aje-support-input" value={support} onChange={(e) => setSupport(e.target.value)} placeholder="Support reference (required)..." className="flex-1 bg-zinc-900 border border-white/20 rounded-md px-2.5 py-1.5 focus:outline-none focus:border-blue-500" />
          <button data-testid="aje-propose-btn" onClick={doPropose} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md transition">
            Propose
          </button>
        </div>
      </div>
    </div>
  );
};
